import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../../api';
import { launchArtWeaver } from '../../../utils/artweaver';
import ArtDrawingBoard from './ArtDrawingBoard';
import { OpenArtWeaverButton, OpenPdfButton } from './ArtWeaverButtons';
import ArtWorksheetPane from './ArtWorksheetPane';
import ResizableSplit from './ResizableSplit';
import { worksheetOf } from './ArtActivityList';

/**
 * One art lesson's drawing page: its header with the worksheet and ArtWeaver
 * buttons, the worksheet and the drawing board side by side (the bar between
 * them resizes both), and Save to Gallery. Saving uses the same endpoint
 * the desktop ArtWeaver launcher posts to, so the picture is added to the
 * student's gallery and sent for coach review in one go, and also to the coach's
 * Syllabus Tracker when the lesson is in it. `lesson` is a published lesson
 * ({ title, description, items, trackerTopic, sessionNumber }); `onOpenItem(item, lesson)`
 * opens its worksheet.
 */
export default function ArtActivityWorkspace({ lesson, viewTitle, onBack, onOpenItem }) {
  const boardRef = useRef(null);
  const [saving, setSaving] = useState(false);

  const studentId = localStorage.getItem('userId');
  const worksheet = worksheetOf(lesson);
  // A lesson that is a column of the coach's Syllabus Tracker (Computer Apps > Artweaver > Beginners
  // lessons are in "Computer apps"): its drawing also goes there, like Your Practice uploads
  // (CourseContentView).
  const trackerTopic = lesson.trackerTopic || lesson.syllabusTopic;

  const handleOpenArtWeaver = () => {
    launchArtWeaver(studentId);
    toast('Opening ArtWeaver with a prepared JPEG. Draw, press Ctrl+S, then close ArtWeaver to upload it.');
  };

  const handleSaveToGallery = async () => {
    const imageData = boardRef.current?.exportImage();
    if (!imageData) {
      toast('Draw something first!', { icon: '🎨' });
      return;
    }
    if (!studentId) {
      toast.error('Please log in again to save your artwork.');
      return;
    }

    setSaving(true);
    try {
      const response = await api.post(`/api/v2/lms/student/${studentId}/courses/art/artweaver/submit`, {
        imageData,
        title: lesson.title,
        description: `${viewTitle}: ${lesson.title}`,
        ...(trackerTopic && lesson.sessionNumber ? { topic: trackerTopic, sessionNumber: lesson.sessionNumber } : {}),
      });
      toast.success('Saved to your gallery and sent for coach review!');
      if (response?.data?.syllabusSubmission === null) {
        toast.error("It couldn't reach your coach's Syllabus Tracker. Please save it again.");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save your artwork');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="mb-5 flex items-center gap-2 text-lg font-extrabold text-blue-700 hover:text-blue-900"
      >
        <span aria-hidden="true">←</span>
        Back to Activities
      </button>

      <div className="mb-5 flex flex-wrap items-center gap-4">
        <span
          className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-4xl"
          aria-hidden="true"
        >
          🎨
        </span>
        <div className="min-w-[12rem] flex-1">
          <h2 className="text-3xl font-extrabold leading-tight text-slate-900">{lesson.title}</h2>
          {lesson.description && <p className="text-sm font-medium text-gray-500">{lesson.description}</p>}
        </div>
        <div className="flex flex-wrap gap-3">
          <OpenPdfButton title={lesson.title} item={worksheet} onOpen={(item) => onOpenItem(item, lesson)} />
          <OpenArtWeaverButton title={lesson.title} variant="solid" onClick={handleOpenArtWeaver} />
        </div>
      </div>

      <ResizableSplit
        label="Resize the worksheet and the drawing board"
        storageKey="artTherapySplitPercent"
        start={<ArtWorksheetPane item={worksheet} title={lesson.title} />}
        end={<ArtDrawingBoard ref={boardRef} />}
      />

      <div className="mt-5 flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="flex flex-1 items-center gap-3 rounded-2xl bg-sky-50 p-4">
          <span
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-xl"
            aria-hidden="true"
          >
            💡
          </span>
          <p className="text-sm font-semibold text-slate-700">
            Draw, colour and be creative! After finishing, save your artwork. It will be added to your gallery and sent
            for coach review.
          </p>
        </div>
        <button
          type="button"
          onClick={handleSaveToGallery}
          disabled={saving}
          className="flex shrink-0 items-center justify-center gap-2 rounded-xl bg-green-600 px-8 py-4 text-base font-extrabold text-white shadow-sm transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span aria-hidden="true">💾</span>
          {saving ? 'Saving...' : 'Save to Gallery'}
        </button>
      </div>
    </div>
  );
}
